import * as XLSX from "xlsx";
import moment from "moment";

/**
 * Pull the `skippedAccounts` array out of a create-or-update protection response.
 * The backend has returned it both at the top level and nested under
 * `body.value`, so check both shapes.
 * @param {Object} data - Raw response payload.
 * @returns {Array} The skipped accounts, or an empty array when there are none.
 */
export const getSkippedAccounts = (data) => {
  const value = data?.body?.value;
  const skipped = data?.skippedAccounts ?? value?.skippedAccounts;
  return Array.isArray(skipped) ? skipped : [];
};

/**
 * Read how many accounts were actually protected from a create-or-update
 * response, falling back when the backend does not send the count.
 * @param {Object} data - Raw response payload.
 * @param {number} fallback - Count to use when `createdCount` is absent.
 * @returns {number} The number of accounts protected.
 */
export const getCreatedCount = (data, fallback = 0) => {
  const value = data?.body?.value;
  const count = data?.createdCount ?? value?.createdCount;
  return typeof count === "number" ? count : fallback;
};

// Sheet headers stay English regardless of the UI language.
const ACCOUNT_NO_HEADER = "Account No";
const REASON_HEADER = "Reason";

/**
 * Build a two column (account no / reason) sheet from the skipped accounts and
 * trigger an immediate download.
 * @param {Array} skippedAccounts - Entries of `{ accountNo, reason }`.
 * @param {string} fileNamePrefix - Prefix for the generated .xlsx file name.
 */
export const downloadSkippedAccountsExcel = (
  skippedAccounts,
  fileNamePrefix = "skipped-accounts"
) => {
  if (!Array.isArray(skippedAccounts) || skippedAccounts.length === 0) return;

  const rows = skippedAccounts.map((item) => ({
    [ACCOUNT_NO_HEADER]: item?.accountNo ?? "",
    [REASON_HEADER]: item?.reason ?? "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: [ACCOUNT_NO_HEADER, REASON_HEADER],
  });
  worksheet["!cols"] = [{ wch: 20 }, { wch: 45 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Skipped Accounts");
  XLSX.writeFile(
    workbook,
    `${fileNamePrefix}-${moment().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`
  );
};
